import { useCallback, useEffect, useRef, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import {
    GeoJSONStoreFeatures,
    TerraDraw,
    TerraDrawFreehandMode,
    TerraDrawPolygonMode,
    TerraDrawSelectMode,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

import { DrawMode, useMapDrawingProps } from './useMapDrawing.props';

export const useMapDrawing = ({ map, enabled }: useMapDrawingProps) => {
    const [polygons, setPolygons] = useState<GeoJSONStoreFeatures[]>([]);
    const [mode, setMode] = useState<DrawMode>(DrawMode.Polygon);

    const drawRef = useRef<TerraDraw | null>(null);
    const historyRef = useRef<GeoJSONStoreFeatures[][]>([]);

    const getPolygonsSnapshot = useCallback(() => {
        if (!drawRef.current) return [];

        return drawRef.current
            .getSnapshot()
            .filter(f => f.geometry?.type === 'Polygon' && !f.properties?.midPoint);
    }, []);

    const saveSnapshot = useCallback(() => {
        if (!drawRef.current) return;

        const snapshot = cloneDeep(getPolygonsSnapshot());

        if (snapshot.length === 0) return;

        const lastSnapshot = historyRef.current[historyRef.current.length - 1];
        if (
            !lastSnapshot ||
            !isEqual(
                lastSnapshot.map(f => f.geometry),
                snapshot.map(f => f.geometry),
            )
        ) {
            historyRef.current.push(snapshot);
            setPolygons(snapshot);
        }
    }, [getPolygonsSnapshot]);

    const handleDeselect = useCallback(() => {
        if (!drawRef.current) return;
        const snapshot = getPolygonsSnapshot();
        setPolygons(snapshot);
    }, [getPolygonsSnapshot]);

    useEffect(() => {
        if (!map || !enabled) return;

        const draw = new TerraDraw({
            adapter: new TerraDrawGoogleMapsAdapter({ map, lib: window.google.maps }),
            modes: [
                new TerraDrawPolygonMode(),
                new TerraDrawFreehandMode(),
                new TerraDrawSelectMode({
                    flags: {
                        polygon: {
                            feature: {
                                draggable: true,
                                coordinates: { draggable: true, deletable: true },
                            },
                        },
                        freehand: {
                            feature: {
                                draggable: true,
                                coordinates: { draggable: true, deletable: true },
                            },
                        },
                    },
                    styles: { selectionPointWidth: 8 },
                }),
            ],
        });

        draw.start();
        draw.setMode(mode);
        drawRef.current = draw;

        const initialSnapshot = cloneDeep(getPolygonsSnapshot());

        historyRef.current = [initialSnapshot];
        setPolygons(initialSnapshot);

        draw.on('finish', saveSnapshot);

        draw.on('deselect', handleDeselect);

        return () => {
            draw.off('finish', saveSnapshot);
            draw.off('deselect', handleDeselect);
            draw.setMode('static');
            draw.stop();
            drawRef.current = null;
            setPolygons([]);
        };
    }, [map, enabled, saveSnapshot, getPolygonsSnapshot, handleDeselect]);

    useEffect(() => {
        if (!drawRef.current) return;

        drawRef.current.setMode(mode);
    }, [mode]);

    const clear = useCallback(() => {
        if (!drawRef.current) return;

        drawRef.current.clear();
        setPolygons([]);
        historyRef.current = [];
    }, []);

    const undo = useCallback(() => {
        if (!drawRef.current || historyRef.current.length <= 1) return;

        historyRef.current.pop();
        const snapshotToRestore = historyRef.current[historyRef.current.length - 1];
        if (!snapshotToRestore) return;

        drawRef.current.clear();
        drawRef.current.addFeatures(snapshotToRestore);
        setPolygons(snapshotToRestore);
    }, []);

    const importPolygons = useCallback((imported: GeoJSONStoreFeatures[]) => {
        if (!drawRef.current) return;

        const importedPolygons = cloneDeep(imported);

        drawRef.current.clear();
        drawRef.current.addFeatures(importedPolygons);
        setPolygons(importedPolygons);
        historyRef.current = [importedPolygons];
    }, []);

    return { polygons, mode, setMode, clear, undo, importPolygons };
};
